import React, { useEffect, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PolarArea } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

// PDF Generation Constants
const PDF_CONFIG = {
  orientation: "landscape", // A4 landscape orientation
  format: "a4", // A4 paper size
  targetWidth: 1400, // Target width in pixels for content rendering (1400px)
  targetHeight: 990, // Target height in pixels for content rendering (maintains aspect ratio)
  html2canvasScale: 1.5, // Scale factor for html2canvas (1.5x for quality vs file size balance)
  jpegQuality: 0.92, // JPEG compression quality (0.92 = 92% quality)
};

const CHART_OPTIONS = {
  animation: { duration: 0 },
  plugins: {
    ChartDataLabels,
    datalabels: { display: false },
    legend: { display: false },
  },
  scales: {
    r: {
      backgroundColor: "#F3F3FA",
      drawOnChartArea: false,
      min: 0,
      max: 6,
      angleLines: { display: true, color: "white", lineWidth: 2 },
      grid: { color: "white" },
      ticks: { maxTicksLimit: 1, display: false },
      pointLabels: { display: false },
    },
  },
  maintainAspectRatio: true,
  responsive: true,
};

// Main Dashboard page component for PDF
const DashboardPage = ({
  selectedManager,
  leftSideData,
  rightSideData,
  radarData,
  t,
}) => {
  return (
    <div
      className="w-full"
      style={{
        width: "100%",
        backgroundColor: "#ffffff",
        padding: "24px",
      }}
    >
      <div className="w-full">
        <div className="box-white w-full !rounded-b-none">
          <h3 className="title-primary-md mb-1">
            {selectedManager.manager.first_name}{" "}
            {selectedManager.manager.last_name}
          </h3>
          <p className="text-[#686285] w-full mb-6">
            {selectedManager.manager.email}
          </p>
          <div className="lg:flex items-center">
            <div className="lg:w-1/3 lg:pr-3 lg:mb-10 mb-4">
              {leftSideData.map(({ title, palette }) => (
                <div key={title} className={`graph-btn text-${palette}`}>
                  <span className="flex-1">{title}</span>
                  <span className="ml-2 whitespace-nowrap">
                    <span>{`${selectedManager.category_scores[title]?.latest_score}`}</span>{" "}
                    {selectedManager.category_scores[title]?.self_score && (
                      <span className="font-light">
                        {`(${t("anaylze.managers.self_assessed")}: ${
                          selectedManager.category_scores[title]?.self_score
                        })`}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            <div className="lg:w-1/3 lg:px-3 lg:mb-10 mb-4">
              <PolarArea
                className="mx-auto p-2 !w-[200px] !h-[200px]"
                data={radarData}
                options={{
                  ...CHART_OPTIONS,
                  plugins: {
                    ...CHART_OPTIONS.plugins,
                    ChartDataLabels,
                    datalabels: {
                      formatter: () => "",
                      anchor: "end",
                      align: "center",
                      font: {
                        family: "Poppins, sans-serif",
                        weight: "bold",
                      },
                    },
                  },
                  clip: { left: 0, top: 0, right: 0, bottom: 0 },
                }}
              />
            </div>
            <div className="lg:w-1/3 lg:pl-3 lg:mb-10 mb-4">
              {rightSideData.map(({ title, palette }) => (
                <div key={title} className={`graph-btn text-${palette}`}>
                  <span className="flex-1">{title}</span>
                  <span className="ml-2 whitespace-nowrap">
                    <span>{`${selectedManager.category_scores[title]?.latest_score}`}</span>{" "}
                    {selectedManager.category_scores[title]?.self_score && (
                      <span className="font-light">
                        {`(${t("anaylze.managers.self_assessed")}: ${
                          selectedManager.category_scores[title]?.self_score
                        })`}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Individual Feedback page component for PDF
const IndividualFeedbackPage = ({ filteredAndCategorizedFeedback, t }) => {
  return (
    <div
      className="w-full"
      style={{
        width: "100%",
        backgroundColor: "#ffffff",
        padding: "24px",
      }}
    >
      <div className="box-white lg:max-w-full lg:min-w-[66%] !rounded-t-none">
        <div className="w-full">
          <div className="flex justify-between items-center mb-4">
            <h3 className="title-primary-base">
              {t("analyze.company.feedback_title")}
            </h3>
          </div>
          <div className="flex mt-6 flex-wrap">
            {filteredAndCategorizedFeedback.length > 0 ? (
              filteredAndCategorizedFeedback.map(
                ([category, feedbackArray]) => (
                  <div
                    style={{
                      width: `${
                        100 / filteredAndCategorizedFeedback.length
                      }%`,
                    }}
                    key={category}
                  >
                    <div className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285] mr-4 mb-4">
                      <h4 className="title-primary-base mb-3">{category}</h4>
                      <div className="space-y-3">
                        {feedbackArray.length > 0 ? (
                          feedbackArray.map((response, index) => (
                            <div key={index} className="flex justify-between">
                              <div>{response}</div>
                              <div className="text-xs ml-2">
                                {index + 1}/{feedbackArray.length}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-[#999] italic">
                            {t("analyze.managers.no_results")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )
            ) : (
              <div className="w-full">
                <div className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285] mr-4 mb-4">
                  <h4 className="title-primary-base">
                    {t("analyze.managers.no_results")}
                  </h4>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// AI Summary page component for PDF
const AISummaryPage = ({ questions, t }) => {
  return (
    <div
      className="w-full"
      style={{
        width: "100%",
        backgroundColor: "#ffffff",
        padding: "24px",
      }}
    >
      <div className="box-white w-full !rounded-t-none">
        <div className="w-full">
          <div className="flex justify-between items-center mb-4">
            <h3 className="title-primary-base">
              {t("analyze.managers.summary")}
            </h3>
          </div>
          {questions && questions.length > 0 ? (
            <div className="mt-6 w-full">
              <div className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285]">
                <div className="space-y-6">
                  {questions.map((question, index) => (
                    <div
                      key={question.questionId || index}
                      className="p-4 bg-white rounded-lg mb-4"
                    >
                      <h5 className="title-primary-base mb-3 text-[#686285]">
                        {question.questionText}
                      </h5>

                      <div className="mb-3">
                        <strong className="text-[#686285]">
                          {t("analyze.managers.summary_header") ||
                            "Summary of Feedback"}
                        </strong>
                        <div className="whitespace-pre-wrap leading-relaxed mt-2">
                          {question.summary}
                        </div>
                      </div>

                      <p className="italic mb-3">
                        {t("analyze.managers.count_statement", {
                          count: question.count,
                        })}
                      </p>

                      <div className="mt-3">
                        <strong className="text-[#686285]">
                          {t("analyze.managers.recommendation_header") ||
                            "Top Recommendation:"}
                        </strong>
                        <div className="whitespace-pre-wrap leading-relaxed mt-2">
                          {question.recommendation}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full mt-6">
              <div className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285]">
                <h4 className="title-primary-base">
                  {t("analyze.managers.no_results")}
                </h4>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Category page component for PDF
const CategoryPage = ({ category, skillsData, categoryPalette }) => {
  const chartData = useMemo(
    () => ({
      labels: skillsData.skills.map((skill) => skill.title),
      datasets: [
        {
          data: skillsData.skills.map((skill) => skill.average),
          backgroundColor: skillsData.skills.map((skill, index) => {
            const baseColor = categoryPalette?.backgroundColor || "#3B82F6";
            const opacity = 0.4 + index * 0.2;
            return baseColor.replace(/[\d.]+\)$/g, `${opacity})`);
          }),
          borderColor: categoryPalette?.backgroundColor || "#3B82F6",
          borderWidth: 3,
        },
      ],
    }),
    [skillsData, categoryPalette]
  );

  return (
    <div
      className="w-full"
      style={{
        width: "100%",
        backgroundColor: "#ffffff",
        padding: "24px",
      }}
    >
      <div className="w-full p-6">
        <h3 className="title-primary-md mb-3 text-center text-lg">
          {category.title} - Skills Breakdown
        </h3>
        <div className="lg:flex items-center">
          <div className="lg:w-1/2 lg:pr-2 lg:mb-4 mb-3 flex flex-col justify-center">
            {skillsData.skills.map((skill) => (
              <button
                key={skill.title}
                className={`graph-btn transition-all duration-200 hover:scale-[1.02] whitespace-nowrap min-h-[48px] text-${category.palette}`}
              >
                {skill.title}
                <span className="float-right">
                  <span>{(skill.average || 0).toFixed(1)}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="lg:w-1/2 lg:pl-2 lg:mb-4 mb-3 flex flex-col items-center justify-center">
            <PolarArea
              className="mx-auto !w-[250px] !h-[250px] transition-all duration-300"
              data={chartData}
              options={CHART_OPTIONS}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// PDF window content component
const PDFWindowContent = ({
  categoriesData,
  selectedManager,
  calculateSkillsData,
  piePalette,
  leftSideData,
  rightSideData,
  radarData,
  filteredAndCategorizedFeedback,
  managerSummary,
  t,
  pdfOptions,
  onClose,
}) => {
  const dashboardPageRef = useRef(null);
  const individualFeedbackPageRef = useRef(null);
  const aiSummaryPagesRefs = useRef([]);
  const categoryPagesRefs = useRef([]);

  // Get categories with skills for rendering
  const categoriesWithSkills = useMemo(() => {
    if (!categoriesData || !selectedManager || !calculateSkillsData) return [];

    return categoriesData
      .filter((category) => category.skills && category.skills.length > 0)
      .map((category) => {
        const skillsData = calculateSkillsData(category, selectedManager);
        if (!skillsData) return null;

        const categoryPalette = piePalette?.find(
          (p) => p.name === category.palette
        );

        return { category, skillsData, categoryPalette };
      })
      .filter(Boolean);
  }, [categoriesData, selectedManager, calculateSkillsData, piePalette]);

  // Get AI summary question chunks (max 2 questions per page)
  const aiSummaryQuestionChunks = useMemo(() => {
    if (
      !managerSummary ||
      !managerSummary.questions ||
      managerSummary.questions.length === 0
    )
      return [];

    const chunks = [];
    for (let i = 0; i < managerSummary.questions.length; i += 2) {
      chunks.push(managerSummary.questions.slice(i, i + 2));
    }
    return chunks;
  }, [managerSummary]);

  // Convert canvas elements to images
  const convertCanvasToImage = (element) => {
    if (!element) return element;

    const canvases = element.querySelectorAll("canvas");
    canvases.forEach((canvas) => {
      const img = document.createElement("img");
      img.src = canvas.toDataURL("image/png");

      const style = window.getComputedStyle(canvas);
      const hasMxAuto = canvas.className.includes("mx-auto");

      Object.assign(img.style, {
        width: style.width,
        height: style.height,
        display: style.display,
        margin: style.margin,
        marginLeft: hasMxAuto ? "auto" : style.marginLeft,
        marginRight: hasMxAuto ? "auto" : style.marginRight,
        padding: style.padding,
        maxWidth: style.maxWidth,
        maxHeight: style.maxHeight,
      });
      img.className = canvas.className;
      canvas.parentNode?.replaceChild(img, canvas);
    });

    return element;
  };

  // Generate PDF using jsPDF + html2canvas
  const generatePDF = async () => {
    if (!selectedManager) return;

    try {
      const pdf = new jsPDF({
        orientation: PDF_CONFIG.orientation,
        unit: "mm",
        format: PDF_CONFIG.format,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Capture and add page to PDF
      const captureAndAddPage = async (element) => {
        if (!element) return;

        await convertCanvasToImage(element);

        const rect = element.getBoundingClientRect();
        const elementWidth = rect.width || PDF_CONFIG.targetWidth;
        const elementHeight = rect.height || PDF_CONFIG.targetHeight;

        const canvas = await html2canvas(element, {
          scale: PDF_CONFIG.html2canvasScale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          width: elementWidth,
          height: elementHeight,
          allowTaint: false,
          letterRendering: true,
        });

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Calculate scale to fit page
        const scaleX = pageWidth / canvasWidth;
        const scaleY = pageHeight / canvasHeight;
        const scale = Math.min(scaleX, scaleY);

        const finalWidth = canvasWidth * scale;
        const finalHeight = canvasHeight * scale;
        const xOffset = (pageWidth - finalWidth) / 2;
        const yOffset = 0;

        const imgData = canvas.toDataURL("image/jpeg", PDF_CONFIG.jpegQuality);
        pdf.addImage(
          imgData,
          "JPEG",
          xOffset,
          yOffset,
          finalWidth,
          finalHeight
        );
      };

      // Add all pages
      await captureAndAddPage(dashboardPageRef.current);

      // Add individual feedback page
      pdf.addPage();
      await captureAndAddPage(individualFeedbackPageRef.current);

      // Add AI summary pages (only if summary exists)
      for (const element of aiSummaryPagesRefs.current) {
        if (element) {
          pdf.addPage();
          await captureAndAddPage(element);
        }
      }

      // Add category pages
      for (const element of categoryPagesRefs.current) {
        if (element) {
          pdf.addPage();
          await captureAndAddPage(element);
        }
      }

      pdf.save(pdfOptions.filename || "survey_results.pdf");
      onClose?.();
    } catch (error) {
      console.error("Error generating PDF:", error);
      onClose?.();
    }
  };

  // Auto-generate PDF when component mounts
  useEffect(() => {
    if (!selectedManager) return;

    const generatePDFAsync = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await generatePDF();
    };

    const timeoutId = setTimeout(generatePDFAsync, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedManager]);

  return (
    <div className="bg-white" style={{ width: "1400px" }}>
      <div ref={dashboardPageRef}>
        <DashboardPage
          selectedManager={selectedManager}
          leftSideData={leftSideData}
          rightSideData={rightSideData}
          radarData={radarData}
          t={t}
        />
      </div>

      <div ref={individualFeedbackPageRef}>
        <IndividualFeedbackPage
          filteredAndCategorizedFeedback={filteredAndCategorizedFeedback}
          t={t}
        />
      </div>

      {aiSummaryQuestionChunks.map((questionChunk, index) => (
        <div
          key={`ai-summary-${index}`}
          ref={(el) => {
            aiSummaryPagesRefs.current[index] = el;
          }}
        >
          <AISummaryPage questions={questionChunk} t={t} />
        </div>
      ))}

      {categoriesWithSkills.map(
        ({ category, skillsData, categoryPalette }, index) => (
          <div
            key={category.title}
            ref={(el) => {
              categoryPagesRefs.current[index] = el;
            }}
          >
            <CategoryPage
              category={category}
              skillsData={skillsData}
              categoryPalette={categoryPalette}
            />
          </div>
        )
      )}
    </div>
  );
};

export default PDFWindowContent;
